/**
 * © 2025 Grupo Salamander SAPI de CV
 * Author: Pablo Adoue Peralta
 *
 * Main i18n service that provides translation functionality with locale management.
 * Implements Open/Closed Principle by allowing extension through configuration.
 *
 * */

import type {
  Ti18nConfig,
  Ti18nLocale,
  Ti18nParams,
  Ti18nTranslationProvider,
} from '../types'

export class I18nService {
  private currentLocale: Ti18nLocale
  private readonly provider: Ti18nTranslationProvider
  private readonly config: Ti18nConfig

  constructor(provider: Ti18nTranslationProvider, config: Ti18nConfig = {}) {
    this.provider = provider
    this.config = {
      defaultLocale: 'es-mx',
      missingKeyBehavior: 'placeholder',
      ...config,
    }
    this.currentLocale = this.config.defaultLocale!
  }

  locale(locale?: Ti18nLocale): Ti18nLocale {
    if (locale) {
      this.currentLocale = locale
    }
    return this.currentLocale
  }

  getLocale(): Ti18nLocale {
    return this.currentLocale
  }

  t(key: string, params?: Ti18nParams): string {
    let translation = this.provider.getTranslation(key, this.currentLocale)

    if (!translation) {
      translation = this.handleMissingKey(key)
    }

    return params ? this.interpolate(translation, params) : translation
  }

  exists(key: unknown): boolean {
    if (typeof key !== 'string') {
      return false
    }
    return this.provider.getTranslation(key, this.currentLocale) !== undefined
  }

  private handleMissingKey(key: string): string {
    switch (this.config.missingKeyBehavior) {
      case 'throw':
        throw new Error(`Translation key not found: ${key}`)
      case 'guess':
        return key
      case 'placeholder':
      default:
        return `$${key}`
    }
  }

  private interpolate(template: string, params: Ti18nParams): string {
    return Object.entries(params).reduce((result, [key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g')
      return result.replace(regex, String(value ?? ''))
    }, template)
  }
}
